import { ChartPanel } from "@/components/analytics";
import { KpiReadout } from "@/components/ui/kpi-readout";

interface ConfusionMatrixData {
  macro: { f1: number; recall: number; precision: number };
  counts: number[][];
  labels: string[];
  weighted: { f1: number; recall: number; precision: number };
  per_class: Array<{ f1: number; label: number; recall: number; precision: number }>;
}

interface ConfusionMatrixVizProps {
  data: ConfusionMatrixData;
}

export function ConfusionMatrixViz({ data }: ConfusionMatrixVizProps) {
  if (!data?.counts || !data?.labels) {
    return <p className="text-xs text-muted">No confusion matrix data available.</p>;
  }

  return (
    <ChartPanel title="Confusion Matrix">
      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-4">
          <KpiReadout label="Macro F1" value={data.macro.f1.toFixed(3)} accent />
          <KpiReadout label="Precision" value={data.macro.precision.toFixed(3)} />
          <KpiReadout label="Recall" value={data.macro.recall.toFixed(3)} />
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr>
                <th className="border border-slate-700 bg-slate-800 p-2" />
                <th
                  className="border border-slate-700 bg-slate-800 p-2"
                  colSpan={data.labels.length}
                >
                  Predicted
                </th>
              </tr>
              <tr>
                <th className="border border-slate-700 bg-slate-800 p-2">Actual</th>
                {data.labels.map((label) => (
                  <th key={label} className="border border-slate-700 bg-slate-800 p-2">
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.counts.map((row, i) => (
                <tr key={data.labels[i] ?? `row-${i}`}>
                  <th className="border border-slate-700 bg-slate-800 p-2">{data.labels[i]}</th>
                  {row.map((count, j) => (
                    <td
                      key={`${data.labels[i] ?? i}-${data.labels[j] ?? j}`}
                      className="border border-slate-700 p-2 text-center"
                      style={{
                        backgroundColor: i === j ? "rgba(59, 130, 246, 0.2)" : "transparent",
                      }}
                    >
                      {count}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </ChartPanel>
  );
}
